import { useEffect, useState } from "react";
import type { DeliverabilityInsight } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { ChartLegend, HourlyChart, type HourlySeries } from "@/components/charts/HourlyChart";

type Loadable<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

/**
 * Insights → Deliverability. "What failed to deliver and why?"
 * Three widgets driven by one server-side aggregation:
 *   1. Headline rate + 24h hourly stacked-bar chart (delivered vs failed)
 *   2. Top failure codes table (errorCode, sample message, count, share)
 *   3. Top senders by volume (from, total, delivered, failed, rate)
 */
export function DeliverabilityPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [insight, setInsight] = useState<Loadable<DeliverabilityInsight>>({
    status: "loading",
  });

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setInsight({ status: "loading" });

    void api.insights
      .deliverability(scope)
      .then((data) => {
        if (!cancelled) setInsight({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setInsight({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  return (
    <div className="space-y-8">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Insights
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Deliverability
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
          {scopeLabel(ctx)} · Last 24 hours.
        </p>
      </header>

      {insight.status === "loading" && <LoadingState />}
      {insight.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {insight.message}
        </p>
      )}
      {insight.status === "ready" && insight.data.summary.total === 0 && <EmptyState />}
      {insight.status === "ready" && insight.data.summary.total > 0 && (
        <>
          <SummaryRow data={insight.data} />
          <DeliverabilityHourlyChart hourly={insight.data.hourly} />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <FailuresTable data={insight.data} />
            </div>
            <div className="lg:col-span-2">
              <SendersTable data={insight.data} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function SummaryRow({ data }: { data: DeliverabilityInsight }) {
  const s = data.summary;
  const cards: Array<{ label: string; value: string; hint: string }> = [
    {
      label: "Delivery rate · 24h",
      value: s.deliveryRate === null ? "—" : `${(s.deliveryRate * 100).toFixed(1)}%`,
      hint:
        s.deliveryRate === null
          ? "0 messages reached terminal state"
          : `${formatInt(s.delivered)} of ${formatInt(s.delivered + s.failed)}`,
    },
    {
      label: "Delivered · 24h",
      value: formatInt(s.delivered),
      hint: "Status = delivered",
    },
    {
      label: "Failed · 24h",
      value: formatInt(s.failed),
      hint: "failed + undelivered",
    },
    {
      label: "Total · 24h",
      value: formatInt(s.total),
      hint: "All projected statuses",
    },
  ];
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]"
        >
          <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {c.label}
          </div>
          <div className="mt-1.5 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            {c.value}
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

function DeliverabilityHourlyChart({ hourly }: { hourly: DeliverabilityInsight["hourly"] }) {
  const series: HourlySeries[] = [
    {
      label: "Delivered",
      fill: "rgb(52 211 153 / 0.85)",
      values: hourly.map((b) => b.delivered),
    },
    {
      label: "Failed",
      fill: "rgb(251 113 133 / 0.9)",
      values: hourly.map((b) => b.failed),
    },
    {
      label: "Other",
      fill: "rgb(113 113 122 / 0.75)",
      values: hourly.map((b) => Math.max(0, b.total - b.delivered - b.failed)),
    },
  ];
  return (
    <section className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-5 dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <header className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Hourly volume · last 24h
          </h2>
          <p className="text-[11.5px] text-zinc-500">
            Stacked: delivered + failed (incl. undelivered).
          </p>
        </div>
        <ChartLegend series={series.slice(0, 2)} />
      </header>
      <HourlyChart
        hours={hourly.map((b) => b.hour)}
        series={series}
        ariaLabel="Hourly delivery volume over the last 24 hours"
      />
    </section>
  );
}

// ---------------------------------------------------------------------------

function FailuresTable({ data }: { data: DeliverabilityInsight }) {
  return (
    <section>
      <header className="mb-2 flex items-end justify-between">
        <div>
          <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
            Top failure reasons
          </h2>
          <p className="text-[11.5px] text-zinc-500">
            Grouped by Twilio error code · share of all failures.
          </p>
        </div>
        <a
          href="https://www.twilio.com/docs/api/errors"
          target="_blank"
          rel="noreferrer"
          className="text-[11.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
        >
          Twilio error reference →
        </a>
      </header>
      {data.failures.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center text-[13px] text-zinc-600 dark:text-zinc-400">
          No failures in the last 24 hours.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
              <tr>
                <th className="px-4 py-2.5">Code</th>
                <th className="px-4 py-2.5">Sample message</th>
                <th className="px-4 py-2.5 text-right">Count</th>
                <th className="px-4 py-2.5 text-right">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
              {data.failures.map((f) => (
                <tr
                  key={f.errorCode}
                  className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60"
                >
                  <td className="px-4 py-3 align-top">
                    <a
                      href={`https://www.twilio.com/docs/api/errors/${f.errorCode}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                    >
                      {f.errorCode}
                    </a>
                  </td>
                  <td className="max-w-[28ch] px-4 py-3 align-top text-[12.5px] text-zinc-700 dark:text-zinc-300">
                    <div className="truncate">{f.sampleMessage ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3 align-top text-right font-mono text-[12.5px] text-zinc-800 dark:text-zinc-200">
                    {formatInt(f.count)}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <SharePill share={f.share} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SharePill({ share }: { share: number }) {
  const pct = Math.round(share * 100);
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
        <span
          className="block h-full bg-rose-500 dark:bg-rose-400/70"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </span>
      <span className="font-mono text-[11.5px] text-zinc-700 dark:text-zinc-300">{pct}%</span>
    </span>
  );
}

// ---------------------------------------------------------------------------

function SendersTable({ data }: { data: DeliverabilityInsight }) {
  return (
    <section>
      <header className="mb-2">
        <h2 className="text-[13.5px] font-semibold text-zinc-900 dark:text-zinc-100">
          Top senders by volume
        </h2>
        <p className="text-[11.5px] text-zinc-500">Outbound performance grouped by `from` value.</p>
      </header>
      {data.senders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center text-[13px] text-zinc-600 dark:text-zinc-400">
          No outbound senders in the last 24 hours.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
              <tr>
                <th className="px-4 py-2.5">Sender</th>
                <th className="px-4 py-2.5 text-right">Volume</th>
                <th className="px-4 py-2.5 text-right">Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
              {data.senders.map((s) => (
                <tr key={s.from} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
                  <td className="px-4 py-3 align-top">
                    <div className="font-mono text-[12.5px] text-zinc-900 dark:text-zinc-100">
                      {s.from}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {formatInt(s.delivered)} delivered · {formatInt(s.failed)} failed
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top text-right font-mono text-[12.5px] text-zinc-800 dark:text-zinc-200">
                    {formatInt(s.total)}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <RatePill rate={s.deliveryRate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RatePill({ rate }: { rate: number | null }) {
  if (rate === null) return <span className="text-[11.5px] text-zinc-500">—</span>;
  const pct = rate * 100;
  // Tier coloring — give the eye a fast read on healthy / warn / fail.
  const tone =
    pct >= 95
      ? {
          ring: "ring-emerald-500/30",
          text: "text-emerald-700 dark:text-emerald-300",
          bg: "bg-emerald-50 dark:bg-emerald-500/10",
        }
      : pct >= 80
        ? {
            ring: "ring-amber-500/30",
            text: "text-amber-700 dark:text-amber-300",
            bg: "bg-amber-50 dark:bg-amber-500/10",
          }
        : {
            ring: "ring-rose-500/30",
            text: "text-rose-700 dark:text-rose-300",
            bg: "bg-rose-50 dark:bg-rose-500/10",
          };
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11.5px] font-medium ring-1 ring-inset " +
        tone.bg +
        " " +
        tone.ring +
        " " +
        tone.text
      }
    >
      {pct.toFixed(1)}%
    </span>
  );
}

// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none p-4"
          >
            <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="mt-3 h-7 w-16 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="mt-2 h-3 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
        ))}
      </div>
      <div className="h-[200px] animate-pulse rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-10 text-center">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        No message traffic in the last 24 hours
      </h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Deliverability insights show up here once messages start flowing through your connected
        Twilio accounts.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function scopeLabel(ctx: ReturnType<typeof useConnectionContext>): string {
  if (ctx.connections.length === 0) return "No accounts connected yet";
  if (ctx.allSelected)
    return `Across all ${ctx.connections.length} ${ctx.connections.length === 1 ? "account" : "accounts"}`;
  if (ctx.selectedIds.length === 1) {
    const c = ctx.connections.find((x) => x.id === ctx.selectedIds[0]);
    return `Scoped to ${c?.friendlyName ?? c?.accountSid ?? "1 account"}`;
  }
  return `Scoped to ${ctx.selectedIds.length} of ${ctx.connections.length} accounts`;
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}
